import { useEffect, useState } from 'react'

import { activeKeySet, type PieSegment } from '../../lib/composition'

interface CompositionPieProps {
  segments: PieSegment[]
  /** Arc denominator. When 0 (or all segments 0) the pie renders an empty grey ring + "No data". */
  total: number
  /** Headline KPI shown in the middle. `value` is '—' when the metric is null. */
  center: { value: string; label: string }
  /** Highlighted segment key(s); '' or [] = all full opacity. Accepts one key or many. */
  active?: string | readonly string[]
  /** Omit for a read-only pie (mandal scope): the legend renders as static chips, not buttons. */
  onToggle?: (key: string) => void
}

const R = 46
const C = 2 * Math.PI * R

/**
 * Generic composition (multi-slice) donut: arcs sized by `count/total`, the headline KPI in the
 * centre, a legend below. Interactive when `onToggle` is set (each arc + chip toggles its key and
 * dims the rest). Null-safe: `total <= 0` shows the grey track + a muted "No data" legend.
 */
export function CompositionPie({ segments, total, center, active = '', onToggle }: CompositionPieProps) {
  // Draw-in entrance: flip mounting false after first paint so arcs transition from empty to full.
  const [mounting, setMounting] = useState(true)
  useEffect(() => {
    setMounting(false)
  }, [segments])

  const activeSet = activeKeySet(active)
  let offset = 0
  const hasData = total > 0 && segments.some((s) => s.count > 0)
  return (
    <div className="flex flex-none flex-col items-center gap-2">
      <div className="relative h-[104px] w-[104px]">
        <svg viewBox="0 0 120 120" className="h-full w-full" role="img" aria-label={`${center.label} ${center.value}`}>
          <circle cx={60} cy={60} r={R} fill="none" stroke="#eef2f8" strokeWidth={13} />
          {hasData &&
            segments.map((s) => {
              if (s.count <= 0) {
                return null
              }
              const len = (s.count / total) * C
              const dash = `${len} ${C - len}`
              const off = offset
              offset += len
              const drawInOffset = mounting ? -(C + off) : -off
              return (
                <g key={s.key}>
                  <circle
                    cx={60}
                    cy={60}
                    r={R}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={13}
                    strokeDasharray={dash}
                    strokeDashoffset={drawInOffset}
                    transform="rotate(-90 60 60)"
                    opacity={activeSet === null || activeSet.has(s.key) ? 1 : 0.25}
                    className="motion-reduce:transition-none"
                    style={{
                      transition: 'stroke-dashoffset 550ms cubic-bezier(.2,.7,.3,1), opacity .2s',
                      ...(onToggle ? { cursor: 'pointer' } : {}),
                    }}
                  >
                    <title>{`${s.label}: ${s.count}${onToggle ? ' — click to filter' : ''}`}</title>
                  </circle>
                  {/* Transparent wide hit-area so the arc itself (not just the legend chip) toggles the facet. */}
                  {onToggle && (
                    <circle
                      cx={60}
                      cy={60}
                      r={R}
                      fill="none"
                      stroke="#000"
                      strokeOpacity={0}
                      strokeWidth={22}
                      strokeDasharray={dash}
                      strokeDashoffset={-off}
                      transform="rotate(-90 60 60)"
                      pointerEvents="stroke"
                      style={{ cursor: 'pointer' }}
                      onClick={() => onToggle(s.key)}
                    />
                  )}
                </g>
              )
            })}
        </svg>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-[26px] font-extrabold tabular-nums">{center.value}</span>
          <span className="text-muted-text text-[8.5px] font-bold tracking-wider uppercase">{center.label}</span>
        </div>
      </div>
      <div className="flex flex-wrap justify-center gap-1">
        {!hasData ? (
          <span className="text-muted-text text-[10.5px] font-semibold">No data</span>
        ) : (
          segments.map((s) =>
            onToggle ? (
              <button
                key={s.key}
                type="button"
                onClick={() => onToggle(s.key)}
                aria-pressed={activeSet?.has(s.key) ?? false}
                className={`flex items-center gap-1.5 rounded-md border px-1.5 py-1 text-[10.5px] font-semibold ${
                  activeSet?.has(s.key) ? 'border-slate-300 bg-slate-100 text-slate-900' : 'border-transparent text-slate-600 hover:bg-slate-50'
                }`}
              >
                <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                {s.label} <b className="tabular-nums">{s.count}</b>
              </button>
            ) : (
              <span key={s.key} className="flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[10.5px] font-semibold text-slate-600">
                <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                {s.label} <b className="tabular-nums">{s.count}</b>
              </span>
            ),
          )
        )}
      </div>
    </div>
  )
}
